import { useState } from 'react';
import { Building2, Pencil, Plus, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useCreateDepartment,
  useDeleteDepartment,
  useDepartments,
  useUpdateDepartment,
} from '@/features/hrm/employees/queries';
import type { Department, DepartmentInput } from '@/features/hrm/employees/types';

import { DepartmentFormDialog } from '../components/DepartmentFormDialog';

export function DepartmentsPage() {
  const { data: departments, isPending } = useDepartments();
  const [editing, setEditing] = useState<Department | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Department | null>(null);

  const create = useCreateDepartment();
  const update = useUpdateDepartment(editing?.id ?? 0);
  const remove = useDeleteDepartment();

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }
  function openEdit(dept: Department) {
    setEditing(dept);
    setDialogOpen(true);
  }
  function handleSubmit(input: DepartmentInput) {
    return editing ? update.mutateAsync(input) : create.mutateAsync(input);
  }

  const rows = departments ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Departments</h1>
          <p className="text-muted-foreground text-sm">
            Org units and their codes — the code feeds employee codes.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="size-4" /> Add department
        </Button>
      </div>

      {isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-10 text-center text-sm">
          <Building2 className="mx-auto mb-2 size-6" />
          No departments yet — add your first one.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((dept) => (
                <TableRow key={dept.id}>
                  <TableCell className="font-medium">{dept.name}</TableCell>
                  <TableCell>
                    {dept.code ? (
                      <Badge variant="outline" className="font-normal">
                        {dept.code}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-xs truncate text-sm">
                    {dept.description || '—'}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" aria-label="Edit" onClick={() => openEdit(dept)}>
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Delete"
                      onClick={() => setToDelete(dept)}
                    >
                      <Trash2 className="text-destructive size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <DepartmentFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        department={editing ?? undefined}
        onSubmit={handleSubmit}
      />

      <AlertDialog open={toDelete != null} onOpenChange={(o) => (o ? null : setToDelete(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{toDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Employees keep their records but lose this department link. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
